import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { get } from '../../../api/client';
import { todayString } from '../../../utils/dateUtils';
import type { PaginationMeta } from '../../../types';

interface ArrivalOrder {
  id: string;
  orderNumber: string | null;
  orderStatus: string | null;
  reviewStatus: string | null;
  customer: { fullName: string | null } | null;
  contactPerson: string | null;
  activityAddress: string | null;
  arrivalDateTime: string;
  orderStartDate: string;
  orderTimeStart: string | null;
  isFullDay: boolean | null;
  serviceType: { name: string | null } | null;
  orderAmount: number | null;
}

function getArrivalInfo(arrivalDateTime: string): {
  timeStr: string;
  minutesUntil: number;
  isPast: boolean;
  urgency: 'past' | 'urgent' | 'soon' | 'today';
} {
  const arr = new Date(arrivalDateTime);
  const now = new Date();
  const minutesUntil = Math.round((arr.getTime() - now.getTime()) / 60000);
  const timeStr = arr.toLocaleTimeString('zh-CN', { hour: '2-digit', minute: '2-digit' });
  const isPast = minutesUntil < 0;
  let urgency: 'past' | 'urgent' | 'soon' | 'today' = 'today';
  if (isPast) urgency = 'past';
  else if (minutesUntil <= 30) urgency = 'urgent';
  else if (minutesUntil <= 120) urgency = 'soon';
  return { timeStr, minutesUntil, isPast, urgency };
}

const URGENCY_STYLES = {
  past:   { card: 'bg-gray-50 border-gray-100',     badge: 'bg-gray-100 text-gray-500',         dot: 'bg-gray-300',    label: '已过' },
  urgent: { card: 'bg-rose-50 border-rose-200',     badge: 'bg-rose-100 text-rose-600',         dot: 'bg-rose-400',    label: '即将到达' },
  soon:   { card: 'bg-amber-50 border-amber-200',   badge: 'bg-amber-100 text-amber-600',       dot: 'bg-amber-400',   label: '即将到达' },
  today:  { card: 'bg-white border-gray-100',       badge: 'bg-teal-100 text-teal-600',         dot: 'bg-teal-400',    label: '今日到达' },
};

export default function ArrivalReminders() {
  const navigate = useNavigate();
  const [orders, setOrders] = useState<ArrivalOrder[]>([]);
  const [loading, setLoading] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const [dismissed, setDismissed] = useState<Set<string>>(new Set());

  const fetchTodayArrivals = useCallback(async () => {
    setLoading(true);
    try {
      const today = todayString();
      const res = await get<{ data: ArrivalOrder[], pagination: PaginationMeta }>('/orders', {
        params: {
          dateFrom: today,
          dateTo: today,
          pageSize: 50
        }
      });
      if (res?.data) {
        // 只保留有到达时间、未取消、未被拒绝的订单
        setOrders(
          res.data.filter(
            o =>
              !!o.arrivalDateTime &&
              o.orderStatus !== '已取消' &&
              o.reviewStatus !== '已拒绝',
          ),
        );
      }
    } catch (error) {
      console.error('获取到达提醒失败:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTodayArrivals();
    // 每5分钟自动刷新
    const timer = setInterval(fetchTodayArrivals, 5 * 60 * 1000);
    return () => clearInterval(timer);
  }, [fetchTodayArrivals]);

  const visibleOrders = orders.filter(o => !dismissed.has(o.id));

  if (!loading && visibleOrders.length === 0) return null;

  const urgentCount = visibleOrders.filter(o => {
    const { urgency } = getArrivalInfo(o.arrivalDateTime);
    return urgency === 'urgent' || urgency === 'soon';
  }).length;

  return (
    <div className={`border rounded-xl overflow-hidden transition-all ${
      urgentCount > 0 ? 'border-rose-200 bg-rose-50/30' : 'border-amber-200 bg-amber-50/20'
    }`}>
      {/* Header */}
      <div
        className={`flex items-center justify-between px-4 py-3 cursor-pointer ${
          urgentCount > 0 ? 'bg-rose-50' : 'bg-amber-50'
        }`}
        onClick={() => setCollapsed(c => !c)}
      >
        <div className="flex items-center gap-2.5">
          <div className={`w-7 h-7 flex items-center justify-center rounded-lg ${urgentCount > 0 ? 'bg-rose-100' : 'bg-amber-100'}`}>
            <i className={`ri-map-pin-time-line text-sm ${urgentCount > 0 ? 'text-rose-600' : 'text-amber-600'}`} />
          </div>
          <div>
            <h3 className={`text-sm font-semibold ${urgentCount > 0 ? 'text-rose-700' : 'text-amber-700'}`}>
              今日到达提醒
            </h3>
            <p className={`text-[10px] mt-0.5 ${urgentCount > 0 ? 'text-rose-500' : 'text-amber-500'}`}>
              今日有 {visibleOrders.length} 个订单设置了预计到达时间
              {urgentCount > 0 && ` · ${urgentCount} 个即将到达`}
            </p>
          </div>
          {urgentCount > 0 && (
            <span className="ml-1 flex items-center gap-1 px-2 py-0.5 bg-rose-100 text-rose-600 text-[10px] font-semibold rounded-full border border-rose-200">
              <span className="w-1.5 h-1.5 rounded-full bg-rose-500 animate-pulse" />
              紧急 {urgentCount}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={e => { e.stopPropagation(); fetchTodayArrivals(); }}
            className={`w-7 h-7 flex items-center justify-center rounded-lg transition-colors cursor-pointer ${
              urgentCount > 0 ? 'text-rose-400 hover:text-rose-600 hover:bg-rose-100' : 'text-amber-400 hover:text-amber-600 hover:bg-amber-100'
            }`}
          >
            <i className="ri-refresh-line text-sm" />
          </button>
          <i className={`ri-arrow-${collapsed ? 'down' : 'up'}-s-line text-base ${urgentCount > 0 ? 'text-rose-400' : 'text-amber-400'}`} />
        </div>
      </div>

      {!collapsed && (
        <div className="p-3 space-y-2">
          {loading ? (
            <div className="flex items-center justify-center py-6 text-gray-400">
              <i className="ri-loader-4-line animate-spin mr-2 text-sm" />
              <span className="text-xs">加载中…</span>
            </div>
          ) : (
            visibleOrders.map(o => {
              const { timeStr, minutesUntil, isPast, urgency } = getArrivalInfo(o.arrivalDateTime);
              const style = URGENCY_STYLES[urgency];
              const displayName = o.customer?.fullName ?? o.contactPerson ?? '未知客户';

              let countdownText: string;
              if (!isPast) {
                if (minutesUntil >= 60) {
                  const h = Math.floor(minutesUntil / 60);
                  const m = minutesUntil % 60;
                  countdownText = m > 0 ? `${h}小时${m}分钟后` : `${h}小时后`;
                } else if (minutesUntil > 0) {
                  countdownText = `${minutesUntil}分钟后`;
                } else {
                  countdownText = '即将到达';
                }
              } else {
                const absMins = Math.abs(minutesUntil);
                if (absMins >= 60) {
                  const h = Math.floor(absMins / 60);
                  countdownText = `已过 ${h}小时`;
                } else {
                  countdownText = `已过 ${absMins}分钟`;
                }
              }

              return (
                <div
                  key={o.id}
                  className={`flex items-start gap-3 p-3 rounded-xl border transition-all ${style.card}`}
                >
                  {/* Time column */}
                  <div className="flex flex-col items-center gap-1 shrink-0 pt-0.5">
                    <div className={`w-8 h-8 flex items-center justify-center rounded-full ${style.badge}`}>
                      <i className="ri-time-line text-sm" />
                    </div>
                    <span className={`text-[10px] font-bold whitespace-nowrap ${
                      urgency === 'urgent' ? 'text-rose-600' :
                      urgency === 'soon' ? 'text-amber-600' :
                      urgency === 'past' ? 'text-gray-400' : 'text-teal-600'
                    }`}>
                      {timeStr}
                    </span>
                  </div>

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-sm font-semibold text-gray-800 truncate">{displayName}</span>
                      {o.serviceType?.name && (
                        <span className="text-[10px] text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded-full shrink-0 whitespace-nowrap">
                          {o.serviceType.name}
                        </span>
                      )}
                    </div>
                    <p className="text-[11px] font-mono text-gray-400 mb-1">
                      {o.orderNumber ?? o.id.slice(0, 8).toUpperCase()}
                      {o.orderAmount != null && ` · ¥${Number(o.orderAmount).toLocaleString('zh-CN')}`}
                    </p>
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full font-medium ${style.badge}`}>
                        <span className={`w-1 h-1 rounded-full ${style.dot} ${!isPast && urgency !== 'today' ? 'animate-pulse' : ''}`} />
                        {countdownText}
                      </span>
                      {o.activityAddress && (
                        <span className="text-[10px] text-gray-400 flex items-center gap-0.5 truncate max-w-[160px]">
                          <i className="ri-map-pin-line text-[10px] shrink-0" />
                          {o.activityAddress}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex flex-col items-end gap-1.5 shrink-0">
                    <button
                      onClick={() => navigate('/orders', { state: { openOrderId: o.id } })}
                      className="text-[10px] text-teal-500 hover:text-teal-600 cursor-pointer transition-colors whitespace-nowrap flex items-center gap-0.5"
                    >
                      查看
                      <i className="ri-arrow-right-s-line text-xs" />
                    </button>
                    <button
                      onClick={() => setDismissed(prev => new Set(prev).add(o.id))}
                      className="text-[10px] text-gray-400 hover:text-gray-600 cursor-pointer transition-colors whitespace-nowrap"
                    >
                      忽略
                    </button>
                  </div>
                </div>
              );
            })
          )}
        </div>
      )}
    </div>
  );
}
